import { FunctionalComponent, h } from "preact";
import { useState, useRef } from "preact/hooks";
import { validate, ValidationRule } from "../../util";
import style from "./style.css";

export interface TextAreaProps {
  label?: string;
  onInput?: Function;
  value: string;
  errors?: string[];
  validation?: ValidationRule;
  onBlur?: Function;
  onFocus?: Function;
  readOnly?: boolean;
  textareaWrapperClass?: string;
  rows?: number;
}

const TextArea: FunctionalComponent<TextAreaProps> = ({
  label,
  onInput,
  value,
  errors = [],
  validation,
  onBlur,
  readOnly,
  textareaWrapperClass,
  onFocus,
  rows = 1,
}: TextAreaProps) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const textareaEl = useRef(null);

  const handleOnInput = (e: Event) => {
    if (validationErrors.length) setValidationErrors([]);
    if (onInput) onInput(e);
  };

  const handleFocus = (isFocused: boolean) => {
    if (onFocus) onFocus();
    setIsFocused(isFocused);
  };

  const handleOnBlur = (e: Event) => {
    handleFocus(false);

    if (onBlur) onBlur(e);
    if (!validation) return;
    if (!label) throw new Error("Must pass label prop with validations");

    const _validationErrors = validate(validation, label, value);
    setValidationErrors(_validationErrors);
  };

  const handleLabelClick = (e: UIEvent) => {
    e.preventDefault();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    textareaEl.current && (textareaEl.current! as HTMLTextAreaElement).focus();
  };

  const hasErrors = errors?.length || validationErrors.length;
  const isActive = value !== "" || isFocused;

  const displayError = () => {
    if (!hasErrors) return;

    return [...validationErrors, ...errors].map((err) => (
      <div key={err} class={style.errorText}>
        {err}
      </div>
    ));
  };

  return (
    <div
      class={`
      ${style.textareaWrapper}
      ${hasErrors ? style.error : ""}
      ${isActive ? style.active : ""}
      ${readOnly ? style.readOnly : ""}
      ${isFocused ? style.isFocused : ""}
      ${textareaWrapperClass}
      `}
    >
      <label onClick={handleLabelClick}>{label}</label>
      <textarea
        rows={rows}
        ref={textareaEl}
        onInput={handleOnInput}
        class={`${style.textarea} ${hasErrors ? style.error : ""}`}
        value={value}
        onFocus={() => handleFocus(true)}
        onBlur={handleOnBlur}
        readOnly={readOnly}
      />
      {displayError()}
    </div>
  );
};

export default TextArea;
